import { Injectable, NgZone } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { EmbedService } from './embed.service';

/** Envelope marker on every message the portal sends down into the product frame. */
const PORTAL_SOURCE = 'edara-portal';
/** Envelope marker on every message this app sends up to the portal. */
const PRODUCT_SOURCE = 'edara-3';

/** A command the portal sends down into this frame. */
export type PortalInboundMessage =
  /**
   * Switch to another organization. `keycloakOrgId` performs the switch (it is what
   * AuthService.switchTenant takes); `tenantId` — the Edara registration org id — is what we
   * COMPARE against, because it is readable straight off the access token, while the Keycloak
   * id of the active org is only known once the async userinfo org list has landed.
   */
  | { type: 'set-organization'; keycloakOrgId: string; tenantId: string }
  /** Apply a UI language without reloading the frame. */
  | { type: 'set-language'; language: string }
  /** Apply the light/dark theme. */
  | { type: 'set-theme'; theme: 'light' | 'dark' }
  /** The portal signed out — drop this frame's local session. The portal owns the IdP logout. */
  | { type: 'logout' }
  /** The user clicked one of the top-bar actions this app published (see [[PortalTopBarAction]]). */
  | { type: 'action'; action: string }
  /**
   * The product was reopened from the portal. The frame stayed mounted while hidden, so this app
   * is still on the page the user left — return to the landing page so opening from the card
   * behaves like opening the app rather than resuming it.
   */
  | { type: 'reset' }
  /**
   * Open a page the user picked in the portal's own menu. Only an in-app router path is ever
   * accepted — see the parser — so a message can never steer this frame off-origin.
   */
  | { type: 'navigate'; url: string };

/**
 * One entry this app asks the portal to show in its header — an action from this app's own top
 * bar that the portal's header has no equivalent for, and which would otherwise be unreachable
 * while embedded.
 */
export interface PortalTopBarAction {
  /** Echoed back verbatim when clicked. */
  id: string;
  /** Material icon name; the portal renders it with its own icon font. */
  icon: string;
  /** Already translated — the portal has no catalogue for this app's i18n keys. */
  label: string;
}

/** An event this app sends up to the portal. */
export type PortalOutboundMessage =
  /** The product finished booting with a usable session. */
  | { type: 'ready'; organization: string | null }
  /** No session inside the frame. Only the portal can fix this — Keycloak refuses to be framed. */
  | { type: 'auth-required' }
  /** The user triggered sign-out from inside the product (keyboard shortcut). */
  | { type: 'logout-request' }
  /** Current in-frame route, so the portal can reflect it in its own URL. */
  | { type: 'navigated'; url: string }
  /**
   * Chrome the portal should adopt while this app is open: the header colour to match the app's
   * own surface, and the top-bar actions to offer. Re-sent whenever theme or language changes.
   */
  | { type: 'chrome'; background: string; color: string; actions: PortalTopBarAction[] };

/**
 * The `postMessage` channel between this app and the Edara Portal that embeds it.
 *
 * Transport only: it validates and emits inbound commands, and sends outbound events to the
 * portal's exact origin — never `'*'`. Applying the commands is the job of [[AppComponent]],
 * which owns the services involved; keeping that out of here avoids dragging AuthService and
 * friends into a service that must be constructible at bootstrap.
 *
 * Entirely inert when not embedded: no listener is attached and every send is a no-op, so the
 * standalone app carries none of this behaviour.
 */
@Injectable({ providedIn: 'root' })
export class PortalBridgeService {

  private readonly inbound$ = new Subject<PortalInboundMessage>();

  constructor(private embedService: EmbedService, private zone: NgZone) {
    if (!this.embedService.isEmbedded) return;
    window.addEventListener('message', this.onMessage);
  }

  /** Commands received from the portal, already origin-checked and shape-checked. */
  public get messages(): Observable<PortalInboundMessage> {
    return this.inbound$.asObservable();
  }

  /** Sends an event up to the portal. No-op when not embedded. */
  public send(message: PortalOutboundMessage): void {
    const target = this.embedService.portalOrigin;
    if (!target) return;
    window.parent.postMessage({ source: PRODUCT_SOURCE, ...message }, target);
  }

  /**
   * Accepts a message only when it comes from the allowlisted portal origin AND carries the
   * portal's envelope marker. The origin check is the security boundary — the marker only
   * separates portal traffic from the unrelated messages third-party SDKs broadcast into the
   * page (Intercom, Firebase, the Angular service worker).
   */
  private readonly onMessage = (event: MessageEvent): void => {
    // Origin is the security boundary; `source` narrows it further to the window that FRAMES us.
    // Another frame on the portal's origin (a sibling iframe, a popup) passes the origin check
    // but is not the portal shell, and must not be able to drive this app.
    if (event.source !== window.parent) return;
    if (event.origin !== this.embedService.portalOrigin) return;

    const data = event.data;
    if (!data || data.source !== PORTAL_SOURCE || typeof data.type !== 'string') return;

    const message = this.parse(data);
    if (!message) return;

    // The listener fires outside Angular's zone, so re-enter it: consumers update view state.
    this.zone.run(() => this.inbound$.next(message));
  };

  /**
   * Reduces a navigation target to an in-app router path, or null for anything else.
   *
   * The portal relays whatever this app's own menu API returns for a page, and that contract is
   * not pinned down here — so a path (`/pos/sales-orders`), a bare relative path, and an absolute
   * URL on THIS origin are all accepted and normalised. What is never accepted is anything that
   * could steer the frame off-origin: a URL on another origin, or a protocol-relative `//host`.
   */
  private toInAppPath(raw: unknown): string | null {
    if (typeof raw !== 'string' || !raw.trim()) return null;
    const value = raw.trim();

    if (/^[a-z][a-z0-9+.-]*:/i.test(value)) {
      // Has a scheme → absolute URL. Only this app's own origin, reduced to its in-app part.
      try {
        const url = new URL(value);
        return url.origin === window.location.origin ? url.pathname + url.search + url.hash : null;
      } catch {
        return null;
      }
    }
    if (value.startsWith('//')) return null;
    return value.startsWith('/') ? value : `/${value}`;
  }

  /** Narrows a raw payload to a known command, dropping anything malformed. */
  private parse(data: any): PortalInboundMessage | null {
    switch (data.type) {
      case 'set-organization':
        return typeof data.keycloakOrgId === 'string' && data.keycloakOrgId
          ? {
            type: 'set-organization',
            keycloakOrgId: data.keycloakOrgId,
            tenantId: typeof data.tenantId === 'string' ? data.tenantId : ''
          }
          : null;
      case 'set-language':
        return typeof data.language === 'string' && data.language
          ? { type: 'set-language', language: data.language }
          : null;
      case 'set-theme':
        return data.theme === 'light' || data.theme === 'dark'
          ? { type: 'set-theme', theme: data.theme }
          : null;
      case 'logout':
        return { type: 'logout' };
      case 'reset':
        return { type: 'reset' };
      case 'navigate': {
        const url = this.toInAppPath(data.url);
        return url ? { type: 'navigate', url } : null;
      }
      case 'action':
        return typeof data.action === 'string' && data.action
          ? { type: 'action', action: data.action }
          : null;
      default:
        return null;
    }
  }
}
